import { type RefObject, useLayoutEffect, useRef, useState } from "react";

/**
 * Locks a frame to the tallest content it ever *settledly* holds. Returns a
 * `ref` to attach to the measured content and the locked `height`. The frame is
 * meant to render with this `height` + `overflow-hidden` so it NEVER resizes
 * while the content animates inside it — content transitions happen within a
 * static frame, not by growing/shrinking it.
 *
 * Seeded with `floor` (e.g. the tallest step's known min height) so the frame
 * fits the largest case from the first paint. A DEBOUNCED `ResizeObserver` then
 * raises the lock only when *settled* content (e.g. a picker's full option
 * list) measures taller — never on the transient mid-transition spike (when two
 * steps are briefly co-mounted), which the fixed frame simply clips. `pad` is
 * added to each measurement (e.g. room for a light-bar that sits just below the
 * last button).
 *
 * Optional second element (`extraRef`): a sibling pinned to the BOTTOM of the
 * frame via `mt-auto` (e.g. the verify action bar), which is NOT part of the
 * measured stack. When it's present (`extraActive`), the locked height is
 * instead `stackHeight + extraGap + extraHeight`, so the frame is always tall
 * enough to seat both with at least `extraGap` of gap between them — the bar can
 * never clip and the `mt-auto` gap can never collapse below `extraGap`. Both
 * elements are measured at their *natural* height (neither is stretched), so
 * there's no feedback loop with the lock.
 */
export function useLockedFrameHeight<T extends HTMLElement, E extends HTMLElement = HTMLElement>({
    floor = 0,
    pad = 0,
    extraGap = 0,
    extraActive = false,
    debounceMs = 250
}: {
    floor?: number;
    pad?: number;
    /** Minimum gap reserved between the measured stack and `extraRef`. */
    extraGap?: number;
    /** Whether `extraRef` is currently mounted (re-subscribes the observer). */
    extraActive?: boolean;
    debounceMs?: number;
} = {}): { ref: RefObject<T | null>; extraRef: RefObject<E | null>; height: number } {
    const ref = useRef<T>(null);
    const extraRef = useRef<E>(null);
    const [height, setHeight] = useState(floor);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        let timer: number | undefined;
        const measure = (): void => {
            window.clearTimeout(timer);
            timer = window.setTimeout(() => {
                const extra = extraRef.current;
                const candidate =
                    extra !== null ? el.scrollHeight + extraGap + extra.offsetHeight : el.scrollHeight + pad;
                setHeight((prev) => Math.max(prev, candidate));
            }, debounceMs);
        };
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        if (extraRef.current) observer.observe(extraRef.current);
        // Re-measure on (re)subscribe — `extraActive` flipping means the bar
        // just mounted/unmounted, so the candidate height changed.
        measure();
        return (): void => {
            window.clearTimeout(timer);
            observer.disconnect();
        };
    }, [pad, extraGap, extraActive, debounceMs]);
    return { ref, extraRef, height };
}
